import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Card,
  Empty,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { InvoiceRow } from '@velora/contracts';
import { productApi } from '../../../api';

/**
 * Admin surface — Defaulters. Real overdue invoices bucketed by escalation
 * band. Concession cases will be excluded once the concessions module ships;
 * for now every open invoice past due-date shows here.
 */
export function DefaultersPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const q = useQuery({
    queryKey: ['invoices', institution, { status: 'open', overdue_only: 'true' }],
    queryFn: () => productApi.listInvoices(institution, { status: 'open', overdue_only: 'true' }),
    enabled: !!institution,
  });

  const rows = q.data?.rows ?? [];
  const total = q.data?.total ?? 0;
  const totalOwed = rows.reduce((a, r) => a + (r.amount_paise - r.paid_paise), 0);
  const bands = {
    early: rows.filter((r) => r.days_overdue <= 7).length,
    mid: rows.filter((r) => r.days_overdue > 7 && r.days_overdue <= 30).length,
    late: rows.filter((r) => r.days_overdue > 30 && r.days_overdue <= 60).length,
    critical: rows.filter((r) => r.days_overdue > 60).length,
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Overdue fees</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          {total} open invoice{total === 1 ? '' : 's'} past due · ₹{paise(totalOwed)} outstanding.
          Escalation policies + concession exclusions ship with the full Fees module.
        </Typography.Text>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="≤ 7 days late" value={bands.early} /></Card>
        <Card size="small"><Statistic title="8–30 days" value={bands.mid} valueStyle={{ color: '#a35800' }} /></Card>
        <Card size="small"><Statistic title="31–60 days" value={bands.late} valueStyle={{ color: '#b3382f' }} /></Card>
        <Card size="small"><Statistic title="> 60 days" value={bands.critical} valueStyle={{ color: '#7a221b' }} /></Card>
      </div>

      <Card size="small" title={`${total} overdue`}>
        {q.isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
        {q.isError && <Alert type="error" showIcon message="Could not load invoices" />}
        {q.data && rows.length === 0 && (
          <Empty description="No overdue invoices. If nothing was ever billed, generate invoices from Fee structures." style={{ padding: 20 }} />
        )}
        {rows.length > 0 && (
          <Table<InvoiceRow>
            size="small"
            pagination={{ pageSize: 15 }}
            rowKey="id"
            dataSource={rows}
            columns={[
              { title: 'Student', key: 's', render: (_, r) => <><b>{r.student_name}</b> <Typography.Text type="secondary" style={{ fontSize: 12 }}>· {r.admission_number} · {r.grade}{r.section ? `-${r.section}` : ''}</Typography.Text></> },
              { title: 'Head', dataIndex: 'fee_head_name', key: 'h' },
              { title: 'Owed', key: 'o', align: 'right', render: (_, r) => <b>₹{paise(r.amount_paise - r.paid_paise)}</b> },
              { title: 'Due', dataIndex: 'due_on', key: 'due', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              {
                title: 'Days late',
                dataIndex: 'days_overdue',
                key: 'd',
                render: (v: number) => (
                  <Tag color={v > 60 ? 'error' : v > 30 ? 'error' : v > 7 ? 'warning' : 'default'}>{v}d</Tag>
                ),
              },
            ]}
          />
        )}
      </Card>
    </Space>
  );
}

function paise(n: number): string {
  return (n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}
